import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { debounce, toast } from "../lib/utils";
import { isLockedToOther } from "../components/CheckInOut";

const Ctx = createContext(null);

// Top-level mutable sections that the backend accepts via PUT.
const PERSISTED_KEYS = [
  "meta", "tasks", "raids", "requirements", "actions", "budget",
  "stakeholders", "milestones", "gaps", "aiOutputs", "s4hana",
  "checkout", "checkInOutLog", "budgetHistory",
  "governance", "fitToStandard", "dataMigration", "integration",
  "testing", "ocm", "cutover", "valueRealization", "ipLibrary", "aiLayer",
  "integrations",
  "documents",
  "baseline",
];

export function ProjectProvider({ projectId, readOnly = false, shareToken = null, children }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const docRef = useRef(null);
  // Pending changed keys accumulated between debounced flushes.
  const dirtyRef = useRef(new Set());
  // Last persisted reference values (per top-level section) — used to diff.
  const baselineRef = useRef({});

  // Load
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError("");
    const load = readOnly && shareToken
      ? api.getShared(projectId, shareToken)
      : api.get(projectId);
    load.then(d => {
      if (!alive) return;
      setDoc(d);
      docRef.current = d;
      // Initialize baseline so the first mutate diffs against the loaded doc.
      const base = {};
      PERSISTED_KEYS.forEach(k => { base[k] = d?.[k]; });
      baselineRef.current = base;
      dirtyRef.current = new Set();
      setLoading(false);
    }).catch(e => {
      if (!alive) return;
      setLoading(false);
      setLoadError(e?.response?.data?.detail || e?.message || "Failed to load project");
    });
    return () => { alive = false; };
  }, [projectId, readOnly, shareToken]);

  // Debounced auto-save: only sends sections that actually changed since the last flush.
  const persist = useMemo(() => debounce(async () => {
    const snap = docRef.current;
    if (!snap) return;
    const dirty = Array.from(dirtyRef.current);
    if (dirty.length === 0) return;
    // Snapshot the keys we're about to flush so concurrent mutations stay queued.
    dirtyRef.current = new Set();
    const payload = {};
    dirty.forEach(k => { payload[k] = snap[k]; });
    setSaving(true);
    try {
      await api.update(snap.id, payload);
      // Mark these sections as the new baseline.
      dirty.forEach(k => { baselineRef.current[k] = snap[k]; });
    } catch (e) {
      // Rollback dirty flags so the next mutation re-queues them.
      dirty.forEach(k => dirtyRef.current.add(k));
      toast("Save failed: " + (e?.response?.data?.detail || e.message || "unknown"));
    } finally {
      setSaving(false);
    }
  }, 700), []);

  const mutate = useCallback((updater, opts = {}) => {
    if (readOnly) { toast("Read-only view"); return; }
    // Enforce check-out lock unless caller explicitly bypasses (used by CheckInOut itself).
    if (!opts.bypassLock && isLockedToOther(docRef.current)) {
      toast(`Locked to ${docRef.current?.checkout?.email} — check in first`);
      return;
    }
    setDoc(prev => {
      if (!prev) return prev;
      const next = typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
      // Compute the set of changed persisted sections (reference comparison —
      // every immutable update produces a new reference, which is the convention
      // already followed throughout the codebase).
      PERSISTED_KEYS.forEach(k => {
        if (next[k] !== baselineRef.current[k]) dirtyRef.current.add(k);
      });
      docRef.current = next;
      persist();
      return next;
    });
  }, [persist, readOnly]);

  const replaceDoc = useCallback((nextDoc) => {
    setDoc(nextDoc);
    docRef.current = nextDoc;
    // Reset baseline so we don't re-flush every field after a fresh fetch.
    const base = {};
    PERSISTED_KEYS.forEach(k => { base[k] = nextDoc?.[k]; });
    baselineRef.current = base;
    dirtyRef.current = new Set();
  }, []);

  return (
    <Ctx.Provider value={{ doc, loading, loadError, saving, mutate, replaceDoc, readOnly }}>
      {children}
    </Ctx.Provider>
  );
}

export function useProject() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useProject must be used inside ProjectProvider");
  return v;
}
